import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ForceGraph2D from 'react-force-graph-2d';
import { Box, Button, VStack, Text, Heading, IconButton, HStack, useToast, Wrap, WrapItem, Badge} from '@chakra-ui/react';
import { AddIcon, MinusIcon, RepeatIcon } from '@chakra-ui/icons';
import { react_api } from "../config";
import { HTTP_STATUS_CODES } from "../GrinsCostant";
import logout from "../services/authService";
import createAxiosInstance from "../interceptors/useAxiosInterceptors";
import { useTranslation } from "react-i18next";
import { useSession } from "../SessionContext";
import { useNavigate } from "react-router-dom";
import SpinnerXL from "./SpinnerXL";
import { MdArrowBack } from "react-icons/md";

const MetadataField = ({ label, value }) => {
    if (Array.isArray(value)) {
        return (
            <Box mb={2} w="100%">
                <Text as="span" fontWeight="bold" color="teal">{label}: </Text>
                <Wrap spacing={1} mt={1}>
                    {value.length === 0 ? (
                        <Text as="span" fontSize="sm" fontStyle="italic"> - </Text>
                    ) : (
                        value.map((item, idx) => (
                            <WrapItem key={idx}>
                                {typeof item === 'object' ? (
                                    <Badge color="white" bg="rgba(23, 141, 161, 1)"borderRadius="full" px={2} py={1}>
                                        {item.name || item.description || JSON.stringify(item)}
                                    </Badge>
                                ) : (
                                    <Badge color="white" bg="rgba(23, 141, 161, 1)" borderRadius="full" px={2} py={1}>
                                        {String(item)}
                                    </Badge>
                                )}
                            </WrapItem>
                        ))
                    )}
                </Wrap>
            </Box>
        );
    }
    // Gestione degli oggetti JSON
    if (value && typeof value === 'object') {
        return (
            <Box mb={2} w="100%">
                <Text as="span" fontWeight="bold" color="teal">{label}: </Text>
                <Box mt={1} p={2} bg="gray.50" borderRadius="md" fontSize="sm" borderLeft="3px solid" borderColor="teal.300">
                    {Object.entries(value).map(([key, val]) => (
                        <Box key={key} ml={val && typeof val === 'object' ? 0 : 0} mb={1}>
                            <Text as="span" fontWeight="medium">{key}:</Text>
                            {val && typeof val === 'object' ? (
                                <Box ml={3} mt={1}>
                                    {Object.entries(val).map(([nestedKey, nestedVal]) => (
                                        <Text key={nestedKey} fontSize="sm">
                                            <Text as="span" fontWeight="medium">{nestedKey}: </Text>
                                            {String(nestedVal)}
                                        </Text>
                                    ))}
                                </Box>
                            ) : (
                                <Text as="span" ml={1}>{String(val)}</Text>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }
    // Gestione dei valori null o undefined
    if (value === null || value === undefined) {
        return (
            <Box mb={2} w="100%">
                <Text as="span" fontWeight="bold" color="teal">{label}: </Text>
                <Text as="span" fontSize="sm" fontStyle="italic"> - </Text>
            </Box>
        );
    }
    // Caso standard per valori semplici
    return (
        <Box mb={2} w="100%">
            <Text as="span" fontWeight="bold" color="teal">{label}: </Text>
            <Text as="span">{String(value)}</Text>
        </Box>
    );
};

function cleanLabel(rawKey) {
    let label = rawKey.replace(/_/g, ' ');
    label = label.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
    label = label.replace(/\w\S*/g, word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
    return label;
}

function useWindowSize() {
    const [size, setSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () =>
            setSize({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return size;
}

export default function SemanticGraph() {
    const { state } = useLocation(); 
    const { t } = useTranslation();
    const [tableID, setTableID] = useState(null);
    const [graphData, setGraphData] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [spinnerLoading, setSpinnerLoading] = useState(false);
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const navigate = useNavigate();
    const { updateSessionData, clearSessionData } = useSession();
    const axiosInstance = createAxiosInstance(async (axiosInstance) => {
        logout(t, clearSessionData, navigate, toast, axiosInstance);
    });
    const toast = useToast();
    const fgRef = useRef();  
    const hasZoomedRef = useRef(false);
    const dataRef = useRef(null);
    // const parentMap = useRef({});  
    const ancestorsMap  = useRef({});  
    const initialGraphRef = useRef(null);
    const { width, height } = useWindowSize();
    const expansionsRef = useRef({});
    const initialCam = useRef({ k: 4 });
// se si vuole ritornare allo zoon nativo rimuovere questa sezione, onWheel, enableZoomInteraction e enablePanInteraction
    const MIN_ZOOM = 1;
    const MAX_ZOOM = 30;
    const handleWheel = e => {
        e.preventDefault();  
        const delta = -e.deltaY; 
        const zoomFactor = 1 + delta * 0.001;  
        const curK = fgRef.current.zoom();
        let newK = curK * zoomFactor;
        newK = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newK));
        fgRef.current.zoom(newK);
    };
//

	useEffect(() => {
        if (graphData === initialGraphRef.current && fgRef.current) {
            const { x, y, k } = initialCam.current;
            fgRef.current.centerAt(x, y, 0);
            fgRef.current.zoom(k, 0);
        }  
        if (expandedNodes.size>0 ) {
            setTimeout(() => fgRef.current.zoomToFit(400, 100));
        }
    }, [graphData]);

    useEffect(() => {
        setTableID(null);
        // visualizzaMetadati(state.data.tableId);
        const tableId = state?.data?.tableId;
        if (tableId) {
            visualizzaMetadati(tableId);
        }
    }, []);

    useEffect(() => {
        if (graphData) {
            dataRef.current = graphData;
        }
    }, [graphData]);

    const handleEngineStop = () => {
        if (!initialCam.current.kSaved) {
            initialCam.current.k = fgRef.current.zoom();
            initialCam.current.kSaved = true;
        }
    };

    const visualizzaMetadati = async (tableId) => {
        setTableID(tableID);
        try {
            setSpinnerLoading(true);
            const url = `${react_api}/getMetadataADP/${tableId}`;
            const res = await axiosInstance.get(url);
            if (res.status.toString() === HTTP_STATUS_CODES.OK) {
                const meta = res.data;
                const root = { nodes: [{ id: meta.tableId, label: meta.dataset_id, type: 'dataset', metadata: meta }], links: [] };
                initialGraphRef.current = root;
                ancestorsMap.current[root.nodes[0].id] = new Set();
                initialGraphRef.original = JSON.parse(JSON.stringify(root));
                setGraphData(root);
            } else throw new Error(t('catalog.dataset.throwNewError.dataRecoveryError'));
        } catch (err) {
            toast({ title: t('toastMsg.callError'), description: String(err), status: 'warning', duration: 4000, isClosable: true });
        }finally{
            setSpinnerLoading(false);
        }
    };

    const handleNodeClick = node => setSelectedNode(node);

    const handleNodeExpand = async (node) => {
        if (!graphData) return;

        // const parentId = parentMap.current[node.id];

        const existingIds = new Set(graphData.nodes.map(n => n.id));
        const existingLinkPairs = new Set(
            graphData.links.map(l => {
                const src = typeof l.source === 'object' ? l.source.id : l.source;
                const tgt = typeof l.target === 'object' ? l.target.id : l.target;
                return `${src}->${tgt}`;
            })
        );
        try {
            setSpinnerLoading(true);

            
            const res = await axiosInstance.post(
                `${react_api}/externalService/getSemanticLinkAndMetadataADP`, {
                tableId: node.id,
                BearerToken: sessionStorage.getItem('token'),
                username: sessionStorage.getItem("username"),
            });
            const result = res.data;
            if (result.status !== HTTP_STATUS_CODES.OK) {
                setSpinnerLoading(false);
                const toastStatus = result.status === "600" ? "info" : "error";
                const toastTitle = result.status === "600" ? "Info" : t("toastMsg.error");
                expansionsRef.current[node.id] = { nodeIds: [], linkKeys: [] };
                toast({
                    title: toastTitle,
                    description: t(`messageBE.${result.message}`),
                    position: "top",
                    status: toastStatus,
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }

            const items = res.data.data;
            const newNodes = [];
            const newLinks = [];
            const currAncestors = ancestorsMap.current[node.id] || new Set();
            items.forEach(item => {
                const tgtId = item.metadata.tableId;
                // if (tgtId === node.id || tgtId === parentId) return;
                if (tgtId === node.id || currAncestors.has(tgtId)) return;
                const propId = `${node.id}_prop_${item.property.name}_${tgtId}`;
                const linkId = `${node.id}_link_${item.link.name}_${tgtId}`;

                // parentMap.current[tgtId] = node.id;
                ancestorsMap.current[tgtId] = new Set(currAncestors);
                ancestorsMap.current[tgtId].add(node.id);

                if (!existingIds.has(propId)) {
                    newNodes.push({
                        id: propId,
                        label: item.property.name,
                        type: 'property',
                        metadata: item.property 
                    });
                    existingIds.add(propId);
                }
                if (!existingIds.has(linkId)) {
                    newNodes.push({
                        id: linkId,
                        label: item.link.name,
                        type: 'link',
                        metadata: item.link
                    });
                    existingIds.add(linkId);
                }
                if (!existingIds.has(tgtId)) {
                    newNodes.push({
                        id: tgtId,
                        label: item.metadata.dataset_id,
                        type: 'dataset',
                        metadata: item.metadata
                    });
                    existingIds.add(tgtId);
                }

                const triples = [
                    [node.id, propId],
                    [propId, linkId],
                    [linkId, tgtId]
                ];

                triples.forEach(([src, dst]) => {
                    const key = `${src}->${dst}`;
                    if (
                        !existingLinkPairs.has(key) &&    
                        existingIds.has(src) &&            
                        existingIds.has(dst)              
                    ) {
                        newLinks.push({ source: src, target: dst });
                        existingLinkPairs.add(key);
                    }
                });
            });
            const addedNodeIds = newNodes.map(n => n.id);
            const addedLinkKeys = newLinks.map(l => {
                const src = typeof l.source === 'object' ? l.source.id : l.source;
                const dst = typeof l.target === 'object'   ? l.target.id : l.target;
                return `${src}->${dst}`;
            });

            expansionsRef.current[node.id] = {
                nodeIds: addedNodeIds,
                linkKeys: addedLinkKeys
            };
            if (newNodes.length === 0 && newLinks.length === 0) {
                expansionsRef.current[node.id] = { nodeIds: [], linkKeys: [] };
                toast({
                    title: t("toastMsg.semanticGraph.noAdditionalLinks"),
                    description: t("toastMsg.semanticGraph.infoNoSemanticRelationships"),
                    status: 'info',
                    position: "top",
                    duration: 4000,
                    isClosable: true,
                });
                setSpinnerLoading(false);
                return;
            }

            fgRef.current.d3ReheatSimulation();

            setGraphData(prev => ({
                nodes: [...prev.nodes, ...newNodes],
                links: [...prev.links, ...newLinks]
            }));
            setExpandedNodes(prev => new Set(prev).add(node.id));

        }
        catch (err) {
            toast({
                title: t('toastMsg.callError'),
                description: String(err),
                status: 'warning',
                duration: 3000,
                isClosable: true
            });
        }finally{
            setSpinnerLoading(false);
        }
    };

    // const toggleExpand = () => {
    //     if (!selectedNode) return;

    //     const id = selectedNode.id;
    //     const rootId = initialGraphRef.current.nodes[0].id;
    //     if (expandedNodes.has(id) && id === rootId) {
    //         setGraphData(initialGraphRef.current);
    //         setExpandedNodes(new Set());
    //         setSelectedNode(null);
    //         parentMap.current = {};
    //         hasZoomedRef.current = false;
    //         expansionsRef.current = {};
    //         return; 
    //     }

    //     if (!expandedNodes.has(id)) {
    //         handleNodeExpand(selectedNode);
    //     }else{
    //         const toRemoveNodes = new Set();
    //         const toRemoveLinkKeys = new Set();
    //         const stack = [id];
            
    //         while (stack.length) {
    //             const curr = stack.pop();
    //             const exp = expansionsRef.current[curr];
    //             if (!exp) continue;
    //                 exp.nodeIds.forEach(nid => {
    //                 toRemoveNodes.add(nid);
    //                 if (expandedNodes.has(nid)) stack.push(nid);
    //             });
    //             exp.linkKeys.forEach(lk => toRemoveLinkKeys.add(lk));
    //             delete expansionsRef.current[curr];
    //         }
    //         setGraphData(prev => ({
    //             nodes: prev.nodes.filter(n => !toRemoveNodes.has(n.id)),
    //             links: prev.links.filter(l => {
    //                 const src = typeof l.source === 'object' ? l.source.id : l.source;
    //                 const dst = typeof l.target === 'object' ? l.target.id : l.target;
    //                 const key = `${src}->${dst}`;
    //                 return (
    //                     !toRemoveLinkKeys.has(key)
    //                     && !toRemoveNodes.has(src)
    //                     && !toRemoveNodes.has(dst)
    //                 );
    //             })
    //         }));
    //         setExpandedNodes(prev => {
    //             const next = new Set(prev);
    //             toRemoveNodes.forEach(nid => next.delete(nid));
    //             next.delete(selectedNode.id);
    //             return next;
    //         });
    //     }
    // };

    const resetGraph = () => {
        if (!initialGraphRef.current) return;
        const fresh = JSON.parse(JSON.stringify(initialGraphRef.original));
        initialGraphRef.current = fresh;
        setGraphData(fresh);
        setExpandedNodes(new Set());
        setSelectedNode(null);
        // parentMap.current = {};
        ancestorsMap.current = {};  
        const rootId = fresh.nodes[0].id;
        ancestorsMap.current[rootId] = new Set();
        hasZoomedRef.current = false;
        expansionsRef.current = {};
        setTimeout(() => centerOnRoot(fgRef, dataRef, initialCam), 0);
    };

    const toggleExpandOrReset = () => {
        if (!selectedNode) return;
        if (expandedNodes.has(selectedNode.id) && selectedNode.type === 'dataset') {
            resetGraph();
        } else if (selectedNode.type === 'dataset') {
        handleNodeExpand(selectedNode);
        }
    };

    const centerOnRoot = (fgRef, dataRef, initialCam) => {
        const rootId = initialGraphRef.current.nodes[0].id;
        const root = dataRef.current.nodes.find(n => n.id === rootId);
        if (root) {
            fgRef.current.centerAt(0, 0, 400);
            fgRef.current.zoom(4, 400);
        }
    };

    const zoom = factor => {
        const cur = fgRef.current.zoom();
        fgRef.current.zoom(cur * factor, 400);
    };

    const fitView = () => {
        const nodes = dataRef.current?.nodes || [];
        if (nodes.length === 1) {
            const node = nodes[0];
            fgRef.current.centerAt(node.x, node.y, 400);
            fgRef.current.zoom(4, 400);
        } else {
            fgRef.current.zoomToFit(400, 100);
        }
    };

    return (
        <Box display="flex" height="100vh" width="100%" position="relative">
            {spinnerLoading && (
                <SpinnerXL />
            )}
            <Box width="80%" bg="gray.50" position="relative" overflow="hidden" onWheel={handleWheel}>
                {graphData && (
                    <ForceGraph2D
                        ref={fgRef}
                        graphData={graphData}
                        enableZoomInteraction={false}
                        enablePanInteraction={true}
                        onEngineStop={handleEngineStop}
                        width={width * 0.8}
                        height={height}
                        linkDistance={60}
                        linkCurvature={0}
                        chargeStrength={-50}
                        warmupTicks={150}
                        cooldownTicks={Infinity} 
                        onNodeClick={handleNodeClick}
                        enableNodeDrag={node => node.type === 'dataset'}
                        onNodeDragStart={node => {
                            if (node.type !== 'dataset') {
                                node.fx = node.x;
                                node.fy = node.y;
                            }
                        }}
                        onNodeDragEnd={node => {
                            if (node.type === 'dataset') {
                                node.fx = node.x;
                                node.fy = node.y;
                            } else {
                                delete node.fx;
                                delete node.fy;
                                node.vx = node.vy = 0;
                                fgRef.current.d3ReheatSimulation();
                            }
                        }}
                        nodePointerAreaPaint={(node, color, ctx) => {
                            ctx.fillStyle = color;
                            ctx.beginPath();
                            if (node.type === 'dataset') {
                                const radius = 12;           
                                ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
                            } else {
                                const w = 18, h = 10, r = 4;
                                ctx.moveTo(node.x - w/2 + r, node.y - h/2);
                                ctx.arcTo(node.x + w/2, node.y - h/2, node.x + w/2, node.y + h/2, r);
                                ctx.arcTo(node.x + w/2, node.y + h/2, node.x - w/2, node.y + h/2, r);
                                ctx.arcTo(node.x - w/2, node.y + h/2, node.x - w/2, node.y - h/2, r);
                                ctx.arcTo(node.x - w/2, node.y - h/2, node.x + w/2, node.y - h/2, r);
                                ctx.closePath();
                            }
                            ctx.fill();
                        }}
                        nodeCanvasObjectMode={() => 'replace'}
                        nodeCanvasObject={(node, ctx) => {
                            const isSelected = selectedNode && node.id === selectedNode.id;
                            const size = 8;
                            ctx.font = '3px Sans-Serif';
                            ctx.textBaseline = 'middle';
                            ctx.textAlign = 'center';

                            if (node.type === 'dataset') {
                                const radius = 12;
                                ctx.beginPath();
                                ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
                                ctx.fillStyle = 'rgba(11,78,90,1)';
                                ctx.fill();
                                ctx.lineWidth = 1;
                                ctx.strokeStyle = isSelected ? 'red' : 'black';
                                ctx.stroke();
                                ctx.fillStyle = 'white';
                                ctx.font = 'bold 4px Sans-Serif';
                                ctx.textAlign = 'center';
                                ctx.textBaseline = 'middle';
                                ctx.fillText(node.label, node.x, node.y);
                            }
                            else {
                                const w = 18, h = 10, r = 4;
                                ctx.beginPath();
                                ctx.moveTo(node.x - w/2 + r, node.y - h/2);
                                ctx.arcTo(node.x + w/2, node.y - h/2, node.x + w/2, node.y + h/2, r);
                                ctx.arcTo(node.x + w/2, node.y + h/2, node.x - w/2, node.y + h/2, r);
                                ctx.arcTo(node.x - w/2, node.y + h/2, node.x - w/2, node.y - h/2, r);
                                ctx.arcTo(node.x - w/2, node.y - h/2, node.x + w/2, node.y - h/2, r);
                                ctx.closePath();
                                ctx.fillStyle = node.type === 'property' ? 'rgba(23, 141, 161, 1)' : '#f90';
                                ctx.fill();
                                ctx.lineWidth = 0.5;
                                ctx.strokeStyle = isSelected ? 'red' : 'black';
                                ctx.stroke();
                                const padding = 0.5; 
                                const maxTextWidth = w - padding * 2;
                                ctx.font = '3px Sans-Serif';
                                ctx.fillStyle = 'white';
                                const truncateText = (text, x, y) => {
                                    let displayText = text;
                                    while (ctx.measureText(displayText + '…').width > maxTextWidth && displayText.length > 0) {
                                        displayText = displayText.slice(0, -1);
                                    }
                                    if (displayText !== text) {
                                        displayText = displayText.slice(0, -1) + '…';
                                    }
                                    ctx.fillText(displayText, x, y);
                                };
                                truncateText(node.label, node.x, node.y);
                            }
                        }}
                        linkCanvasObject={(link, ctx) => {
                            ctx.beginPath();
                            ctx.moveTo(link.source.x, link.source.y);
                            ctx.lineTo(link.target.x, link.target.y);
                            ctx.strokeStyle = 'black';
                            ctx.lineWidth = 0.3;
                            ctx.stroke();
                        }}
                    />
                )}

                {graphData && (
                    <HStack position="absolute" top="4" left="4" spacing="2" zIndex="2">
                        <IconButton size="sm" icon={<AddIcon />} bg="teal" color="white" _hover={{ bg: "teal.400", '& > svg': { color: 'white' } }} onClick={() => zoom(1.2)} />
                        <IconButton size="sm" icon={<MinusIcon />} bg="teal" color="white" _hover={{ bg: "teal.400", '& > svg': { color: 'white' } }} onClick={() => zoom(0.8)} />
                        <IconButton size="sm" icon={<RepeatIcon />} bg="teal" color="white" _hover={{ bg: "teal.400", '& > svg': { color: 'white' } }} onClick={fitView} />
                    </HStack>
                )}
            </Box>

            <Box width="20%" height="100vh" bg="#f7fafc" display="flex" flexDirection="column" justifyContent="flex-start" alignItems="center" pt={4}>
                <VStack width="95%" height="calc(100vh - 48px)" p="4" spacing="4" bg="#f6f3ec" boxShadow="md" borderColor="rgba(23, 141, 161, 1)" borderRadius={5} borderWidth={2} overflowY="auto" alignItems="flex-start">
                    {selectedNode ? (
                        <>
                            <Heading size="md" alignSelf="center" textAlign="center" color="rgba(11,78,90,255)" whiteSpace="normal" wordBreak="break-word" maxW="100%">
                                {!selectedNode.metadata.title? selectedNode.label : selectedNode.metadata.title}
                            </Heading>
                            
                            {/* {selectedNode.type === 'dataset' && <Button colorScheme="teal" size="lg" alignSelf="center" onClick={toggleExpand}>{expandedNodes.has(selectedNode.id) ? t('semanticGraph.compress') : t('semanticGraph.expand')}</Button>} */}

                            {selectedNode.type === 'dataset' && (
                                <HStack spacing={3} alignSelf="center">
                                    <Button
                                        colorScheme="teal"
                                        size="lg"
                                        onClick={() => handleNodeExpand(selectedNode)}
                                        disabled={expandedNodes.has(selectedNode.id) || (expansionsRef.current[selectedNode.id]?.linkKeys.length === 0)}
                                    >
                                        {t('semanticGraph.expand')}
                                    </Button>
                                    <Button colorScheme="teal" size="lg" onClick={resetGraph}>Reset</Button>
                                </HStack>
                            )}
                            <Box w="100%">
                                {(() => {
                                    if (!graphData || !selectedNode) return null;

                                    const rootId = initialGraphRef.current?.nodes[0]?.id;
                                    const isRoot = selectedNode.id === rootId;
                                    const excludeKeys = ['status', 'similarity'];
                                    const metadataEntries = Object.entries(selectedNode.metadata || {})
                                    .filter(([key]) => !excludeKeys.includes(key));
                                    const metadataElements = metadataEntries.map(([key, value]) => {
                                        const label = cleanLabel(key);
                                        return <MetadataField key={key} label={label} value={value} />;
                                    });

                                    let collegamentiElements = null;

                                    if (selectedNode.type === 'property') {
                                        const linkedLinks = graphData.links
                                            .filter(l => {
                                                const src = typeof l.source === 'object' ? l.source.id : l.source;
                                                return src === selectedNode.id;
                                            })
                                            .map(l => {
                                                const linkId = typeof l.target === 'object' ? l.target.id : l.target;
                                                return graphData.nodes.find(n => n.id === linkId);
                                            })
                                            .filter(n => n); 
                                            
                                        if (linkedLinks.length === 0) {
                                            collegamentiElements = (
                                                <Text fontSize="sm" fontStyle="italic" color="gray.500" mt={2}>No connection</Text>
                                            );
                                        } else {
                                            collegamentiElements = (
                                                <>
                                                    <Text fontSize="md" fontWeight="bold" mt={4} mb={2} color="rgba(11,78,90,255)">Connections:</Text>
                                                    {linkedLinks.map(linkNode => (
                                                    <Box key={linkNode.id} mb={2} p={2} bg="white" borderRadius="md" border="1px solid" borderColor="teal.200">
                                                        <Box mb={2} w="100%">
                                                            <Text as="span" fontWeight="bold" color="teal" mt={1}>Name: </Text>
                                                            <Text as="span">{linkNode.label}</Text>
                                                        </Box>
                                                        <Box mb={2} w="100%">
                                                            <Text as="span" fontWeight="bold" color="teal" mt={1}>Description: </Text>
                                                            <Text as="span">{linkNode.metadata?.description || " - "}</Text>
                                                        </Box>
                                                        <Box mb={2} w="100%">
                                                            <Text as="span" fontWeight="bold" color="teal" mt={1}>Similarity : </Text>
                                                            <Text as="span">{linkNode.metadata?.similarity  || " - "}</Text>
                                                        </Box>
                                                    </Box>
                                                    ))}
                                                </>
                                            );
                                        }
                                    }

                                    if (selectedNode.type === 'link') {
                                        const linkedProperties = graphData.links
                                            .filter(l => {
                                                const tgt = typeof l.target === 'object' ? l.target.id : l.target;
                                                return tgt === selectedNode.id;
                                            })
                                            .map(l => {
                                                const propId = typeof l.source === 'object' ? l.source.id : l.source;
                                                return graphData.nodes.find(n => n.id === propId);
                                            })
                                            .filter(n => n); 

                                        if (linkedProperties.length === 0) {
                                            collegamentiElements = (
                                                <Text fontSize="sm" fontStyle="italic" color="gray.500" mt={2}>No connection</Text>
                                            );
                                        } else {
                                            collegamentiElements = (
                                                <>
                                                    <Text fontSize="md" fontWeight="bold" mt={4} mb={2} color="rgba(11,78,90,255)" >Connections :</Text>
                                                    {linkedProperties.map(propNode => (
                                                    <Box key={propNode.id} mb={2} p={2} bg="white" borderRadius="md" border="1px solid" borderColor="teal.200">
                                                        <Box mb={2} w="100%">
                                                            <Text as="span" fontWeight="bold" color="teal" mt={1}>Name: </Text>
                                                            <Text as="span">{propNode.label}</Text>
                                                        </Box>
                                                        <Box mb={2} w="100%">
                                                            <Text as="span" fontWeight="bold" color="teal" mt={1}>Description: </Text>
                                                            <Text as="span">{propNode.metadata?.description || " - "}</Text>
                                                        </Box>
                                                        <Box mb={2} w="100%">
                                                            <Text as="span" fontWeight="bold" color="teal" mt={1}>Similarity : </Text>
                                                            <Text as="span">{propNode.metadata?.similarity  || " - "}</Text>
                                                        </Box>
                                                    </Box>
                                                    ))}
                                                </>
                                            );
                                        }
                                    }
                                    return (
                                        <>
                                            {metadataElements}
                                            {collegamentiElements}
                                        </>
                                    );
                                })()}
                            </Box>
                        </>
                    ) : (
                        <Text alignSelf="center" textAlign="center" fontSize="md" color="rgba(11,78,90,255)" fontWeight="bold">{t('semanticGraph.selectNodeForDetails')}</Text>
                    )}
                </VStack>
                <Box width="100%" p={3}>
                    <Button w="100%" p={3} gap={2} variant="outline" colorScheme="teal" _hover={{bg: "teal.500", color: "white",}} onClick={() => navigate(-1)}><MdArrowBack size={18} />{t('semanticGraph.backToDatasetCatalog')}</Button>
                </Box>
                {/* Bottom margin spacer */}
                <Box width="100%" paddingBottom={24} height="16px" bg="#f7fafc" />
            </Box>
        </Box>
    );
}